import {Order, OrderItem, Product, TicketConfiguration} from '../Models';
import {OrderQuantityRestrictionEnum} from '../Enums';

export class ProductQuantityUtility {
  private readonly order: Order | null = null;

  public constructor(order: Order | null = null) {
    this.order = order;
  }

  reachedMaxPositionQuantity(product: Product, quantity: number) {
    return Boolean(product.maxOrderQuantity)
      && (
        product.maxOrderQuantityRestriction === OrderQuantityRestrictionEnum.POSITION
        && quantity === product.maxOrderQuantity
      );
  }

  reachedMinPositionQuantity(product: Product, quantity: number) {
    return (this.currentTotalOfProduct(product) + quantity) >= product.minOrderQuantity;
  }

  hasMinPositionQuantity(product: Product, quantity: number) {
    return (this.currentTotalOfProduct(product) + quantity) === product.minOrderQuantity;
  }

  reachedMaxOrderQuantity(product: Product, quantity: number) {
    // TODO: check whether there can be multiple order positions of the same product in an order
    return Boolean(product.maxOrderQuantity)
      && (this.currentTotalOfProduct(product) + quantity) === product.maxOrderQuantity;
  }

  currentTotalOfProduct(product: Product) {
    if (this.order) {
      return this.order.orderItems.reduce<number>(
        (a, b) => {
          if (product.id === b.productId) {
            return a + b.quantity;
          }
          return a;
        },
        0,
      );
    }
    return 0;
  }

  cartReachedMaxPositionQuantity(ticketConfiguration: TicketConfiguration) {
    const maxVal = ticketConfiguration.product.maxOrderQuantity;
    return Boolean(maxVal)
      && ticketConfiguration.product.maxOrderQuantityRestriction === OrderQuantityRestrictionEnum.POSITION
      && ticketConfiguration.quantity >= maxVal!;
  }

  cartReachedMinPositionQuantity(ticketConfiguration: TicketConfiguration) {
    // negative -> user increased q, positive -> user decreased q
    const diffQuantity = this.getDiffBetweenConfigAndOrder(ticketConfiguration);
    return (this.cartCurrentTotalOfProduct(ticketConfiguration)) - diffQuantity >= ticketConfiguration.product.minOrderQuantity;
  }

  cartHasMinPositionQuantity(ticketConfiguration: TicketConfiguration) {
    // negative -> user increased q, positive -> user decreased q
    const diffQuantity = this.getDiffBetweenConfigAndOrder(ticketConfiguration);
    return (this.cartCurrentTotalOfProduct(ticketConfiguration) - diffQuantity) === ticketConfiguration.product.minOrderQuantity;
  }

  cartReachedMaxOrderQuantity(ticketConfiguration: TicketConfiguration) {
    const maxVal = ticketConfiguration.product.maxOrderQuantity;
    // TODO: check whether there can be multiple order positions of the same product in an order
    // negative -> user increased q, positive -> user decreased q
    const diffQuantity = this.getDiffBetweenConfigAndOrder(ticketConfiguration);
    return maxVal !== null
      && maxVal !== 0
      && (this.cartCurrentTotalOfProduct(ticketConfiguration) - diffQuantity) >= maxVal;
  }

  cartCurrentTotalOfProduct(ticketConfiguration: TicketConfiguration) {
    if (this.order) {
      const product = ticketConfiguration.product;
      if (!ticketConfiguration.isSubProduct) {
        return this.currentTotalOfProduct(product);
      } else {
        // sub products have no cross-cart constraint, therefore only consider the individual item
        let currentItem = this.findRelatedOrderItem(ticketConfiguration);
        if (currentItem) {
          return currentItem.quantity;
        }
      }
    }
    return 0;
  }

  getDiffBetweenConfigAndOrder(ticketConfiguration: TicketConfiguration): number {
    if (this.order) {
      const relatedOrderItem = this.findRelatedOrderItem(ticketConfiguration);
      if (relatedOrderItem) {
        return relatedOrderItem.quantity - ticketConfiguration.quantity;
      }
    }
    return 0;
  }

  getOrderQuantity(ticketConfiguration: TicketConfiguration): number {
    if (this.order) {
      const relatedOrderItem = this.findRelatedOrderItem(ticketConfiguration);
      if (relatedOrderItem) {
        return relatedOrderItem.quantity;
      }
    }
    return 0;
  }

  findRelatedOrderItem(ticketConfiguration: TicketConfiguration): OrderItem | undefined {
    if (this.order) {
      let orderItem: OrderItem | undefined = undefined;
      this.order.orderItems.forEach(o => {
        if (!orderItem && o.id === ticketConfiguration.id) {
          orderItem = o;
        } else if (!orderItem && o.subOrderItems.length > 0) {
          o.subOrderItems.forEach(so => {
            if (so.id === ticketConfiguration.id) {
              orderItem = so;
            }
          });
        }
      });
      return orderItem;
    }
  }
}
